import { faToggleOff, faToggleOn } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MaterialTable from "material-table";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { Alert, Button, Card, Col, Collapse, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { apiConfigureLoggerSMSNumbers, apiOutstandingLoggerSMSNumbersConfiguration, apiSquirrelEmailsGetList, apiSquirrelEmailsNotifySave, apiSquirrelEmailsSave, apiSquirrelSMSNumbersGetList, apiSquirrelSMSNumbersNotifySave } from "../../../api/apiLoggerConfiguration";
import { apiSquirrelUpdateDarcaHubOfflineNotify, apiSquirrelUpdateLoggerContactAlarmNotify, apiSquirrelsGetList } from "../../../api/apiSquirrels";
import { apiSiteSettingsList } from "../../../api/site";
import { features, isFeatureEnabled } from "../../../config";
import { dynamicSort } from "../../../helpers/sort";
import { tableIcons } from "../../../helpers/tableIcons";
import local from "../../../localization/strings";
import PageTitle from "../../common/PageTitle";
import { toast } from "react-toastify";

// A heading that is always readable, with the detail behind a toggle.
//
// Each of the three lists on this screen does a different job, and the differences between them are the
// thing people get wrong — so every section carries its own explanation rather than sharing one. Collapsed
// by default because someone who already knows the difference is here to edit a row, not to read.
const InfoPanel = ({ heading, className, children }) => {
	const [open, setOpen] = useState(false);

	return (
		<Alert color="info" className={className}>
			<div className="d-flex justify-content-between align-items-center">
				<h6 className="mb-0">{heading}</h6>
				<Button color="link" className="p-0 small" onClick={() => setOpen(!open)} aria-expanded={open}>
					{open ? local.TF_InfoHide : local.TF_InfoShow}
				</Button>
			</div>
			<Collapse isOpen={open}>
				<div className="mt-2">{children}</div>
			</Collapse>
		</Alert>
	);
};

// One cloud alarm, for one contact, in the contact's own row.
//
// These sit in the contact tables rather than in a list of their own, because every one of them is
// about a number or an address that is already named a few columns to the left, and repeating the
// whole list underneath to tick it was the confusing part. The header says Cloud alarms, because the
// rest of each table is the logger's own configuration and these are not: they apply the moment they
// are ticked, while the columns beside them wait for the logger to collect the change.
//
// Live even when the table is read-only. A queued change or an unreachable logger freezes the
// contact list, but who hears about "unable to contact logger" is a Darca Connect setting, and the
// moment a site would most want to change it is the moment its logger has gone quiet.
const RecipientBox = ({ checked, disabled, onChange }) => <input className="form-check-input position-static m-0" type="checkbox" checked={checked === true} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />;

const LoggerSMSNumbers = ({ match, history }) => {
	const [data, setData] = useState();
	const [emailData, setEmailData] = useState();
	const [pending, setPending] = useState();
	const [squirrel, setSquirrel] = useState();
	const [showDarcaHubOfflineWarning, setShowDarcaHubOfflineWarning] = useState(false);
	const [errorPhoneAndEmail, setErrorPhoneAndEmail] = useState(undefined);
	const [errorEmail, setErrorEmail] = useState(undefined);
	const [maxNumber, setMaxNumber] = useState(10);
	const [groups, setGroups] = useState(false);
	const [readonly, setReadonly] = useState(false);
	const [hasPhoneNumber, setHasPhoneNumber] = useState(false);
	const [dirtyPhone, setDirtyPhone] = useState(false);
	const [dirtyEmail, setDirtyEmail] = useState(false);
	const tableRef = useRef();
	const emailTableRef = useRef();
	const sMSCloudRelayEmailAlarmsFeatureEnabled = isFeatureEnabled(features.SMSCloudRelayEmailAlarms);
	const darcaHubOfflineAlarmFeatureEnabled = isFeatureEnabled(features.DarcaHubOfflineAlarm);

	const [columns, setColumns] = useState([
		{
			field: "phoneIndex", //
			title: local.TF_Phone_Index,
			defaultSort: "asc",
			hidden: true,
		},
		{ field: "description", title: local.TS_Description },
		{ field: "phoneNumber", title: local.TS_Phone_Number },
		{ field: "email", title: local.TS_Email, hidden: !sMSCloudRelayEmailAlarmsFeatureEnabled },
		{ field: "group", title: local.TF_Group_Name },
	]);

	const [emailColumns, setEmailColumns] = useState([
		{
			field: "phoneIndex", //
			title: local.TF_Phone_Index,
			defaultSort: "asc",
			hidden: true,
		},
		{ field: "description", title: local.TS_Description },
		{ field: "email", title: local.TS_Email },
		{ field: "group", title: local.TF_Group_Name },
	]);

	const pendingColumns = [
		{
			field: "PhoneIndex", //
			title: local.TF_Phone_Index,
			defaultSort: "asc",
			hidden: true,
		},
		{ field: "Description", title: local.TS_Description },
		{ field: "PhoneNumber", title: local.TS_Phone_Number },
		{ field: "Email", title: local.TS_Email, hidden: !sMSCloudRelayEmailAlarmsFeatureEnabled },
		{ field: "Group", hidden: !groups, title: local.TF_Group_Name, lookup: { " ": "All", "A": "A", "B": "B", "C": "C", "D": "D", "E": "E", "F": "F", "G": "G", "H": "H", "I": "I", "J": "J" } },
	];

	const loadData = useCallback(async () => {
		const sn = match?.params?.serialNumber;

		const squirrels = await apiSquirrelsGetList();
		const foundSquirrel = squirrels.find((x) => x.serialNumber === sn);
		setSquirrel(foundSquirrel);

		const siteSettingsResult = await apiSiteSettingsList();
		if (siteSettingsResult.find((x) => x.settingKey === `${sn}/NoConnection` && x.settingValue === "0")) {
			//No Action
		} else {
			setReadonly(true);
		}

		//Grouping
		if (getBit(foundSquirrel.specialDarca, 1) === 1) {
			setGroups(true);
			//Extended Grouping
			if (getBit(foundSquirrel.specialDarca, 2) === 1) {
				setMaxNumber(20);
			} else {
				setMaxNumber(10);
			}
		} else {
			setMaxNumber(10);
			setGroups(false);
		}
	}, [match]);
	const loadDataSmsNumbers = useCallback(async () => {
		const sn = match?.params?.serialNumber;

		const pendingConfig = await apiOutstandingLoggerSMSNumbersConfiguration(sn);
		if (pendingConfig?.data) {
			setReadonly(true);
			setPending(pendingConfig.data?.SMSNumbers);
		} else {
			setReadonly(false);
		}

		const smsResult = await apiSquirrelSMSNumbersGetList(sn);
		smsResult.forEach((e) => {
			if (!e.email) {
				e.email = "";
			}
		});
		setHasPhoneNumber(smsResult.length > 0);
		setData(smsResult);
	}, [match]);
	const loadDataEmails = useCallback(async () => {
		const sn = match?.params?.serialNumber;

		const emailResult = await apiSquirrelEmailsGetList(sn);
		emailResult.forEach((e) => {
			if (!e.email) {
				e.email = "";
			}
		});

		setEmailData(emailResult);
	}, [match]);

	useEffect(() => {
		const initialLoadData = async () => {
			await loadData();
			await loadDataSmsNumbers();
			await loadDataEmails();
		};

		initialLoadData();
	}, [loadData, loadDataSmsNumbers, loadDataEmails]);

	useEffect(() => {
		let lookup = { " ": "All" };
		//Append lettered properties for groups
		for (let i = 65; i < 65 + maxNumber; i++) {
			lookup[String.fromCharCode(i)] = String.fromCharCode(i);
		}
		setColumns((prevColumns) => {
			const newColumns = prevColumns.map((column) => {
				if (column.field === "group") {
					return { ...column, lookup: lookup };
				}
				return column;
			});
			return newColumns;
		});
		setEmailColumns((prevColumns) => {
			const newColumns = prevColumns.map((column) => {
				if (column.field === "group") {
					return { ...column, lookup: lookup };
				}
				return column;
			});
			return newColumns;
		});
	}, [maxNumber]);

	useEffect(() => {
		setColumns((prevColumns) => {
			const newColumns = prevColumns.map((column) => {
				if (column.field === "group") {
					return { ...column, hidden: !groups };
				}
				return column;
			});
			return newColumns;
		});
		setEmailColumns((prevColumns) => {
			const newColumns = prevColumns.map((column) => {
				if (column.field === "group") {
					return { ...column, hidden: !groups };
				}
				return column;
			});
			return newColumns;
		});
	}, [groups]);

	// Held in a ref so the column definitions survive a tick. setNumberRecipient closes over `data`,
	// which changes on every click, and rebuilding the columns each time would throw away the table's
	// sort order underneath the user.
	const recipientWrite = useRef({});

	// The Email column is hidden without SMSCloudRelayEmailAlarms, so the heading must not offer one —
	// the same choice the page title already makes between Alarm Contacts and SMS Numbers.
	const phoneTableTitle = sMSCloudRelayEmailAlarmsFeatureEnabled ? local.TF_PhoneNumber_And_OptionalEmail : local.TF_PhoneNumbers;

	// One tick per alarm, not one per channel. A contact with a number and an address beside it is one
	// person, and asking a site to reason about the two separately in a table this wide bought nothing;
	// a site that really wants them split can move the address to the Emails Only list.
	//
	// Both flags are written even where the Email column is hidden. Leaving the email flag behind looks
	// careful — the site cannot see that half, so why touch it — but it strands the two out of step: the
	// box shows the SMS flag, so turning SMSCloudRelayEmailAlarms back on leaves a ticked box whose
	// address is silently refused. Writing both costs nothing while the feature is off, because the
	// _ToSend procedures gate every email branch on it, and means the tick says the same thing whenever
	// the feature returns.
	const cloudAlarms = useMemo(() => [{ title: local.TF_Darca_hub_offline, hidden: !darcaHubOfflineAlarmFeatureEnabled, enabled: !!squirrel?.darcaHubOfflineNotify, sms: "smsDarcaHubOfflineNotify", email: "emailDarcaHubOfflineNotify" }, { title: local.TF_Unable_To_Contact_Logger, hidden: false, enabled: !!squirrel?.loggerContactAlarmNotify, sms: "smsLoggerContactNotify", email: "emailLoggerContactNotify" }], [darcaHubOfflineAlarmFeatureEnabled, squirrel?.darcaHubOfflineNotify, squirrel?.loggerContactAlarmNotify]);

	const alarmColumns = useCallback(
		(emailOnly) =>
			cloudAlarms.map((a) => {
				const field = emailOnly ? a.email : a.sms;
				return {
					field,
					hidden: a.hidden,
					editable: "never",
					sorting: false,
					title: (
						<>
							<div className="text-uppercase fs--2 text-600">{local.TF_CloudAlarms}</div>
							<div>{a.title}</div>
						</>
					),
					render: (row) => {
						// Empty for the table's own add-row, which renders every cell before there is any row
						// data to render it from, and for anything else without a slot. A contact that does
						// not exist yet has nothing to tick: the choice is written against a stored row, so
						// the cell stays blank until the row is really there.
						if (!row || !Number.isInteger(row.phoneIndex)) return null;
						if (!emailOnly && row.isUnsaved) return null;
						return (
							<RecipientBox
								checked={row[field]}
								disabled={!a.enabled}
								onChange={(checked) => {
									if (emailOnly) {
										recipientWrite.current.email(row.phoneIndex, { [a.email]: checked });
									} else {
										recipientWrite.current.number(row.phoneIndex, { [a.sms]: checked, [a.email]: checked });
									}
								}}
							/>
						);
					},
				};
			}),
		[cloudAlarms],
	);

	// Current Values carries the boxes; Pending Changes deliberately does not. A number waiting for the
	// logger has no stored row for a choice to be written to, so there is nothing to tick until it
	// arrives — which is also why nothing here has to track rows added in this session.
	const phoneColumns = useMemo(() => [...columns, ...alarmColumns(false)], [columns, alarmColumns]);
	const emailOnlyColumns = useMemo(() => [...emailColumns, ...alarmColumns(true)], [emailColumns, alarmColumns]);

	const onSavePhoneAndEmail = useCallback(
		async (dataToSave) => {
			setErrorPhoneAndEmail(undefined);

			if (tableRef.current.state.showAddRow || tableRef.current.state.lastEditingRow || emailTableRef.current.state.showAddRow || emailTableRef.current.state.lastEditingRow) {
				setErrorPhoneAndEmail(local.TF_Edit_Inprogress);
				return;
			}

			if (dataToSave.length === 0 && emailData.length > 0) {
				setErrorPhoneAndEmail(local.TF_EmailAlarmsRequirePhoneNumber);
				return;
			}

			for (const item of dataToSave) {
				if (!item.phoneNumber) {
					setErrorPhoneAndEmail(local.TF_Phone_Number_Missing);
					return;
				}
				if (!(item.phoneNumber || "").match(/^[+][1-9][0-9]{6,15}$/) && !(item.phoneNumber || "").match(/^[*]{3}.*$/)) {
					setErrorPhoneAndEmail(local.TF_Phone_Number_Invalid);
					return;
				}
				if ((item.phoneNumber || "").length > 20) {
					setErrorPhoneAndEmail(local.TF_Phone_Number_TooLong);
					return;
				}
				if (sMSCloudRelayEmailAlarmsFeatureEnabled) {
					if ((item.email || "").length > 254) {
						setErrorPhoneAndEmail(local.TF_Email_TooLong);
						return;
					}
					//https://www.regular-expressions.info/email.html
					if (item.email && !item.email.match(/^[A-Z0-9._%+-]{1,64}@(?:[A-Z0-9-]{1,63}\.){1,125}[A-Z]{2,63}$/i)) {
						setErrorPhoneAndEmail(local.TF_Email_Invalid);
						return;
					}
				}
				if ((item.description || "").length > 20) {
					setErrorPhoneAndEmail(local.TF_Phone_Description_TooLong);
					return;
				}
				if (groups && !item.group) {
					setErrorPhoneAndEmail(local.TF_Group_Missing);
					return;
				}
			}

			dataToSave.sort(dynamicSort("phoneIndex"));

			// Two destinations, because these are two different kinds of setting. The numbers, addresses
			// and groups are the logger's configuration and are queued for it to collect; the tick boxes
			// are cloud-only and are written straight away. Saving the tick boxes first means they hold
			// even if the queued configuration is never accepted by the device.
			await apiSquirrelSMSNumbersNotifySave(
				squirrel.serialNumber,
				// The row's own phoneIndex, not the renumbered `key` used below. The stored rows keep the
				// slots they were loaded with until the logger accepts the queued change, so the new
				// numbering does not identify them yet. A row added in this session has no stored row to
				// update and is skipped — it is inserted receiving nothing when the device confirms, and
				// its boxes appear once it is really there.
				dataToSave
					.filter((x) => Number.isInteger(x.phoneIndex) && !x.isUnsaved)
					.map((x) => {
						return {
							phoneIndex: x.phoneIndex,
							smsDarcaHubOfflineNotify: x.smsDarcaHubOfflineNotify === true,
							smsLoggerContactNotify: x.smsLoggerContactNotify === true,
							emailDarcaHubOfflineNotify: x.emailDarcaHubOfflineNotify === true,
							emailLoggerContactNotify: x.emailLoggerContactNotify === true,
						};
					}),
			);

			await apiConfigureLoggerSMSNumbers(
				squirrel.serialNumber,
				dataToSave.map((x, key) => {
					return { phoneIndex: key, phoneNumber: x.phoneNumber, email: x.email ? x.email : null, description: x.description || null, group: x.group, phoneIndexOriginal: x.phoneIndexOriginal };
				}),
			);
			setDirtyPhone(false);
			await loadDataSmsNumbers();
		},
		[groups, loadDataSmsNumbers, squirrel, sMSCloudRelayEmailAlarmsFeatureEnabled, emailData],
	);

	const onSaveEmails = useCallback(
		async (dataToSave) => {
			setErrorEmail(undefined);

			if (tableRef.current.state.showAddRow || tableRef.current.state.lastEditingRow || emailTableRef.current.state.showAddRow || emailTableRef.current.state.lastEditingRow) {
				setErrorEmail(local.TF_Edit_Inprogress);
				return;
			}

			if (!hasPhoneNumber && dataToSave.length > 0) {
				setErrorEmail(local.TF_EmailAlarmsRequirePhoneNumber);
				return;
			}

			for (const item of dataToSave) {
				if ((item.email || "").length > 254) {
					setErrorEmail(local.TF_Email_TooLong);
					return;
				}
				//https://www.regular-expressions.info/email.html
				if (item.email && !item.email.match(/^[A-Z0-9._%+-]{1,64}@(?:[A-Z0-9-]{1,63}\.){1,125}[A-Z]{2,63}$/i)) {
					setErrorEmail(local.TF_Email_Invalid);
					return;
				}
				if ((item.description || "").length > 20) {
					setErrorEmail(local.TF_Phone_Description_TooLong);
					return;
				}
				if (groups && !item.group) {
					setErrorEmail(local.TF_Group_Missing);
					return;
				}
			}

			dataToSave.sort(dynamicSort("phoneIndex"));

			// The notify flags are carried through unchanged. They are set from the Notifications panel,
			// not from this table, but this save rewrites every row — omitting them would reset each
			// address to receiving everything.
			await apiSquirrelEmailsSave(
				squirrel.serialNumber,
				dataToSave.map((x, key) => {
					return {
						phoneIndex: key,
						phoneNumber: x.phoneNumber,
						email: x.email ? x.email : null,
						description: x.description || null,
						group: x.group,
						phoneIndexOriginal: x.phoneIndexOriginal,
						emailDarcaHubOfflineNotify: x.emailDarcaHubOfflineNotify === true,
						emailLoggerContactNotify: x.emailLoggerContactNotify === true,
					};
				}),
			);
			setDirtyEmail(false);
			toast.success(local.TS_Update_Successful);
			await loadDataEmails();
		},
		[groups, loadDataEmails, squirrel, hasPhoneNumber],
	);

	// Recipient tick boxes sit beside the master switches and behave like them: the change is written on
	// the spot, with no Save Changes step. Local state is updated first so the box responds immediately.
	const setNumberRecipient = useCallback(
		async (phoneIndex, fields) => {
			const updated = data.map((x) => (x.phoneIndex === phoneIndex ? { ...x, ...fields } : x));
			setData(updated);
			await apiSquirrelSMSNumbersNotifySave(
				squirrel.serialNumber,
				updated
					.filter((x) => Number.isInteger(x.phoneIndex) && !x.isUnsaved)
					.map((x) => {
						return {
							phoneIndex: x.phoneIndex,
							smsDarcaHubOfflineNotify: x.smsDarcaHubOfflineNotify === true,
							smsLoggerContactNotify: x.smsLoggerContactNotify === true,
							emailDarcaHubOfflineNotify: x.emailDarcaHubOfflineNotify === true,
							emailLoggerContactNotify: x.emailLoggerContactNotify === true,
						};
					}),
			);
		},
		[data, squirrel],
	);

	const setEmailRecipient = useCallback(
		async (phoneIndex, fields) => {
			const updated = emailData.map((x) => (x.phoneIndex === phoneIndex ? { ...x, ...fields } : x));
			setEmailData(updated);
			await apiSquirrelEmailsNotifySave(
				squirrel.serialNumber,
				updated
					.filter((x) => Number.isInteger(x.phoneIndex))
					.map((x) => {
						return {
							phoneIndex: x.phoneIndex,
							emailDarcaHubOfflineNotify: x.emailDarcaHubOfflineNotify === true,
							emailLoggerContactNotify: x.emailLoggerContactNotify === true,
						};
					}),
			);
		},
		[emailData, squirrel],
	);

	// Assigned here rather than beside the ref, because both callbacks are declared above this line and
	// naming them any earlier reads them before they exist. Only the click handlers read it, so this
	// runs in good time.
	recipientWrite.current = { number: setNumberRecipient, email: setEmailRecipient };

	function getBit(number, bitPosition) {
		return (number & (1 << bitPosition)) === 0 ? 0 : 1;
	}

	const checkDarcaHubOfflineNotify = (newValue) => {
		if (!newValue) {
			setShowDarcaHubOfflineWarning(true);
		} else {
			updateDarcaHubOfflineNotify(newValue);
		}
	};

	const updateDarcaHubOfflineNotify = useCallback(
		async (newValue) => {
			setSquirrel((x) => {
				return { ...x, darcaHubOfflineNotify: newValue };
			});
			await apiSquirrelUpdateDarcaHubOfflineNotify(match?.params?.serialNumber, newValue);
			setShowDarcaHubOfflineWarning(false);
		},
		[match],
	);

	const updateLoggerContactAlarmNotify = useCallback(
		async (newValue) => {
			setSquirrel((x) => {
				return { ...x, loggerContactAlarmNotify: newValue };
			});
			await apiSquirrelUpdateLoggerContactAlarmNotify(match?.params?.serialNumber, newValue);
		},
		[match],
	);

	const onClose = () => {
		if (tableRef.current.state.showAddRow || tableRef.current.state.lastEditingRow) {
			setErrorPhoneAndEmail(local.TF_Edit_Inprogress);
			return;
		}
		// Optional chaining: without SMSCloudRelayEmailAlarms the Emails Only table is never rendered,
		// so there is no ref to read on those sites.
		if (emailTableRef.current?.state?.showAddRow || emailTableRef.current?.state?.lastEditingRow) {
			setErrorEmail(local.TF_Edit_Inprogress);
			return;
		}
		console.log(dirtyEmail, dirtyPhone);
		if (dirtyPhone || dirtyEmail) {
			console.log("DIRTY");
		} else {
			history.push(`/site_settings/logger/${match?.params?.serialNumber}`);
		}
	};

	return (
		<>
			<Modal isOpen={showDarcaHubOfflineWarning} toggle={() => setShowDarcaHubOfflineWarning(false)} centered backdrop="static">
				<ModalHeader>
					{local.TF_Darca_hub_offline}
					<span className="float-right close-modal" style={{ position: "absolute", top: 15, right: 20 }} onClick={() => setShowDarcaHubOfflineWarning(false)}>
						X
					</span>
				</ModalHeader>
				<ModalBody>
					<Alert color="danger">{local.TF_Darca_Hub_Offline_ModalAlert}</Alert>
					<h6>{local.TF_Darca_Hub_Offline_Subheading}</h6>
					<p className="small">{local.TF_Darca_Hub_Offline_Explanation}</p>
					<Button size="sm" color="primary" onClick={() => setShowDarcaHubOfflineWarning(false)}>
						{local.TS_Cancel}
					</Button>
					<Button size="sm" color="secondary" onClick={() => updateDarcaHubOfflineNotify(false)} className="ml-2">
						{local.TF_Darca_Hub_Offline_Button}
					</Button>
				</ModalBody>
			</Modal>
			<PageTitle title={`${sMSCloudRelayEmailAlarmsFeatureEnabled ? local.TF_Alarm_Contacts : local.TF_SMS_Numbers}: ${match?.params?.serialNumber || ""}`} />
			{data && (
				<>
					{/* The switches lead, because they are the master for the columns in the tables below
					    them: with one off, its column greys out everywhere and nobody is told whatever the
					    boxes say. */}
					<InfoPanel heading={local.TF_NotificationsHeading} className="mt-4">
						<p className="small mb-1">{local.TF_NotificationsWhat}</p>
						<p className="small mb-0">{local.TF_NotificationsScope}</p>
					</InfoPanel>
					<Card className="p-2 mt-4">
						<h5 className="text-primary">{local.TF_Notifications}</h5>
						{darcaHubOfflineAlarmFeatureEnabled && (
							<div className="d-flex flex-row align-items-center">
								<span className="font-weight-bold mr-2">{local.TF_Darca_hub_offline}:</span>
								<FontAwesomeIcon
									className="text-secondary cursor-pointer"
									icon={squirrel.darcaHubOfflineNotify ? faToggleOn : faToggleOff}
									size="2x"
									onClick={() => {
										checkDarcaHubOfflineNotify(squirrel.darcaHubOfflineNotify ? false : true);
									}}
									title={squirrel.darcaHubOfflineNotify ? local.TF_Notify : local.TF_Notify_Off}
								/>
							</div>
						)}
						<div className="d-flex flex-row align-items-center">
							<span className="font-weight-bold mr-2">{local.TF_Unable_To_Contact_Logger}:</span>
							<FontAwesomeIcon
								className="text-secondary cursor-pointer"
								icon={squirrel.loggerContactAlarmNotify ? faToggleOn : faToggleOff}
								size="2x"
								onClick={() => {
									updateLoggerContactAlarmNotify(!squirrel.loggerContactAlarmNotify);
								}}
								title={squirrel.loggerContactAlarmNotify ? local.TF_Notify : local.TF_Notify_Off}
							/>
						</div>
					</Card>
					{/* One panel per section rather than one for the screen: the three lists do different
					    jobs, and a single notice describing any one of them misrepresents the other two. */}
					<InfoPanel heading={local.TF_PhoneListHeading} className="mt-4">
						<p className="small mb-1">{local.TF_PhoneListWhat}</p>
						<p className="small mb-1">{local.TF_PhoneListQueued}</p>
						{groups && <p className="small mb-1">{local.TF_PhoneListGroups}</p>}
						{sMSCloudRelayEmailAlarmsFeatureEnabled && <p className="small mb-1">{local.TF_PhoneListRelay}</p>}
						<p className="small mb-1">{sMSCloudRelayEmailAlarmsFeatureEnabled ? local.TF_PhoneListTickBoxes : local.TF_PhoneListTickBoxesSmsOnly}</p>
						<p className="small mb-0">{sMSCloudRelayEmailAlarmsFeatureEnabled ? local.TF_PhoneListCost : local.TF_PhoneListCostSmsOnly}</p>
					</InfoPanel>
					<Card className="mt-4">
						<div className="MuiTable-SmallHeader MuiTable-NoShadow">
							<MaterialTable
								title={`${phoneTableTitle}${pending ? ` - ${local.TF_Current_Values}` : ""}`}
								columns={phoneColumns}
								tableRef={tableRef}
								editable={{
									onRowAdd:
										readonly || (data?.length || 0) >= maxNumber
											? undefined
											: (newData) =>
													new Promise((resolve, _reject) => {
														setTimeout(() => {
															setDirtyPhone(true);
															const phoneIndex = data.length > 0 ? Math.max(...data.map((o) => o.phoneIndex)) + 1 : 0;
															newData.phoneIndex = phoneIndex;
															// Until Save Changes moves it to Pending Changes it sits in this very table, and its
															// phoneIndex is only a guess at the next free slot — there is no stored row for
															// SquirrelSMSNumbers_NotifySave to write a choice to. Its Cloud alarms cells stay empty
															// until the logger has accepted it. Dropped on the next load, which comes from the API.
															newData.isUnsaved = true;
															setData([...data, newData]);
															setErrorPhoneAndEmail();
															resolve();
														}, 1000);
													}),
									onRowUpdate: readonly
										? undefined
										: (newData, oldData) =>
												new Promise((resolve, _reject) => {
													setTimeout(() => {
														setDirtyPhone(true);
														const phoneIndex = oldData.phoneIndex;
														newData.phoneIndex = phoneIndex;
														setData([...data.filter((x) => x.phoneIndex !== phoneIndex), newData]);
														setErrorPhoneAndEmail();
														resolve();
													}, 1000);
												}),
									onRowDelete: readonly
										? undefined
										: (oldData) =>
												new Promise((resolve, _reject) => {
													setTimeout(() => {
														setDirtyPhone(true);
														const phoneIndex = oldData.phoneIndex;
														setData([...data.filter((x) => x.phoneIndex !== phoneIndex)]);
														setErrorPhoneAndEmail();
														resolve();
													}, 1000);
												}),
								}}
								data={data}
								icons={tableIcons}
								options={{
									search: false,
									sorting: true,
									paging: false,
									padding: "dense",
									emptyRowsWhenPaging: false,
									showEmptyDataSourceMessage: false,
									headerStyle: { backgroundColor: "#edf2f9", color: "#01579b" },
								}}
							/>
							<Row>
								<Col>
									{!readonly && !pending && dirtyPhone && (
										<>
											<Button type="button" color="primary" className="m-2" onClick={() => onSavePhoneAndEmail(data)}>
												{local.TS_Save_Changes}
											</Button>
										</>
									)}
								</Col>
							</Row>
							<Row>
								<Col>
									{errorPhoneAndEmail && (
										<Alert color="danger" className="mt-2 mb-0">
											{errorPhoneAndEmail}
										</Alert>
									)}
								</Col>
							</Row>{" "}
						</div>
					</Card>
					{pending && (
						<div className="mt-4 MuiTable-SmallHeader">
							<MaterialTable
								columns={pendingColumns}
								data={pending}
								title={`${phoneTableTitle} - ${local.TF_Pending_Changes}`}
								icons={tableIcons}
								options={{
									search: false,
									sorting: true,
									paging: false,
									padding: "dense",
									emptyRowsWhenPaging: false,
									showEmptyDataSourceMessage: false,
									headerStyle: { backgroundColor: "#edf2f9", color: "#01579b" },
								}}
							/>
						</div>
					)}
					{/* Hidden without the feature, because nothing would ever send to these addresses: the
					    _ToSend procedures gate every email branch on SMSCloudRelayEmailAlarms, and the page
					    already calls itself SMS Numbers rather than Alarm Contacts in that case. */}
					{sMSCloudRelayEmailAlarmsFeatureEnabled && (
						<>
							<InfoPanel heading={local.TF_EmailListHeading} className="mt-4">
								<p className="small mb-1">{local.TF_EmailListWhat}</p>
								<p className="small mb-0">{local.TF_EmailListConsequence}</p>
							</InfoPanel>
							<Card className="mt-4">
								<div className="MuiTable-SmallHeader MuiTable-NoShadow">
									<MaterialTable
										title={local.TF_EmailsOnly}
										columns={emailOnlyColumns}
										tableRef={emailTableRef}
										editable={{
											onRowAdd: (newData) =>
												new Promise((resolve, _reject) => {
													setTimeout(() => {
														setDirtyEmail(true);
														const phoneIndex = emailData.length > 0 ? Math.max(...emailData.map((o) => o.phoneIndex)) + 1 : 0;
														newData.phoneIndex = phoneIndex;
														setEmailData([...emailData, newData]);
														setErrorEmail();
														resolve();
													}, 1000);
												}),
											onRowUpdate: (newData, oldData) =>
												new Promise((resolve, _reject) => {
													setTimeout(() => {
														setDirtyEmail(true);
														const phoneIndex = oldData.phoneIndex;
														newData.phoneIndex = phoneIndex;
														setEmailData([...emailData.filter((x) => x.phoneIndex !== phoneIndex), newData]);
														setErrorEmail();
														resolve();
													}, 1000);
												}),
											onRowDelete: (oldData) =>
												new Promise((resolve, _reject) => {
													setTimeout(() => {
														setDirtyEmail(true);
														const phoneIndex = oldData.phoneIndex;
														setEmailData([...emailData.filter((x) => x.phoneIndex !== phoneIndex)]);
														setErrorEmail();
														resolve();
													}, 1000);
												}),
										}}
										data={emailData}
										icons={tableIcons}
										options={{
											search: false,
											sorting: true,
											paging: false,
											padding: "dense",
											emptyRowsWhenPaging: false,
											showEmptyDataSourceMessage: false,
											headerStyle: { backgroundColor: "#edf2f9", color: "#01579b" },
										}}
									/>
									<Row>
										<Col>
											{dirtyEmail && (
												<Button type="button" color="primary" className="m-2" onClick={() => onSaveEmails(emailData)}>
													{local.TS_Save_Changes}
												</Button>
											)}
										</Col>
									</Row>
									<Row>
										<Col>
											{errorEmail && (
												<Alert color="danger" className="mt-2 mb-0">
													{errorEmail}
												</Alert>
											)}
										</Col>
									</Row>
								</div>
							</Card>
						</>
					)}
					<Row>
						<Col className="text-right">
							{dirtyEmail || dirtyPhone ? (
								<Button type="button" color="danger" className="mt-2 ml-2" onClick={() => history.push(`/site_settings/logger/${match?.params?.serialNumber}`)}>
									{local.TF_DiscardChanges}
								</Button>
							) : (
								<Button type="button" color="secondary" className="mt-2 ml-2" onClick={onClose}>
									{local.TS_Close}
								</Button>
							)}
						</Col>
					</Row>
				</>
			)}
		</>
	);
};

export default withRouter(LoggerSMSNumbers);
